import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { AppLayout } from './components/layout/AppLayout';
import { GlobalTimerRibbon } from './components/GlobalTimerRibbon';
import { LoginPage } from './pages/LoginPage';
import { AuthCallback } from './pages/AuthCallback';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ProposalsPage } from './pages/ProposalsPage';
import { ProposalDetailPage } from './pages/ProposalDetailPage';
import { TasksPage } from './pages/TasksPage';
import { TeamPage } from './pages/TeamPage';
import { TimeTrackingPage } from './pages/TimeTrackingPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { CompanyDetailPage } from './pages/CompanyDetailPage';
import { FinancePage } from './pages/FinancePage';
import { ReportsPage } from './pages/ReportsPage';
import { LeaveApplicationsPage } from './pages/LeaveApplicationsPage';
import { ReimbursementsPage } from './pages/ReimbursementsPage';
import './App.css';

function TimerWrapper() {
  const { user } = useAuth();
  if (!user) return null;
  return <GlobalTimerRibbon />;
}

function RoleRoute({ allowedRoles, children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Extracted role constants to avoid inline array creation on every render
const ROLES_ADMIN_MANAGER = ['admin', 'manager'];
const ROLES_ADMIN = ['admin'];
const ROLES_ADMIN_MANAGER_LEAD = ['admin', 'manager', 'team_lead'];

// Wraps a route in ProtectedRoute + (optional RoleRoute) + AppLayout + RouteErrorBoundary
// so any render-time error shows a friendly fallback instead of blanking the page.
function Page({ name, role, children }) {
  const inner = (
    <AppLayout>
      <RouteErrorBoundary routeName={name}>{children}</RouteErrorBoundary>
    </AppLayout>
  );
  return (
    <ProtectedRoute>
      {role ? <RoleRoute allowedRoles={role}>{inner}</RoleRoute> : inner}
    </ProtectedRoute>
  );
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <TimerWrapper />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Page name="Dashboard"><DashboardPage /></Page>} />
        <Route path="/projects" element={<Page name="Projects"><ProjectsPage /></Page>} />
        <Route path="/projects/:projectId" element={<Page name="Project Detail"><ProjectDetailPage /></Page>} />
        <Route path="/proposals" element={<Page name="Proposals" role={ROLES_ADMIN_MANAGER}><ProposalsPage /></Page>} />
        <Route path="/proposals/:proposalId" element={<Page name="Proposal Detail" role={ROLES_ADMIN_MANAGER}><ProposalDetailPage /></Page>} />
        <Route path="/tasks" element={<Page name="Tasks"><TasksPage /></Page>} />
        <Route path="/team" element={<Page name="Team" role={ROLES_ADMIN}><TeamPage /></Page>} />
        <Route path="/time-tracking" element={<Page name="Time Tracking"><TimeTrackingPage /></Page>} />
        <Route path="/clients" element={<Page name="Clients" role={ROLES_ADMIN_MANAGER}><ClientsPage /></Page>} />
        <Route path="/clients/:clientId" element={<Page name="Client Detail" role={ROLES_ADMIN_MANAGER}><ClientDetailPage /></Page>} />
        <Route path="/companies/:companyId" element={<Page name="Company Detail" role={ROLES_ADMIN_MANAGER}><CompanyDetailPage /></Page>} />
        <Route path="/finance" element={<Page name="Finance" role={ROLES_ADMIN_MANAGER_LEAD}><FinancePage /></Page>} />
        <Route path="/reports" element={<Page name="Reports" role={ROLES_ADMIN_MANAGER}><ReportsPage /></Page>} />
        <Route path="/leave-reimbursement" element={<Page name="Leave & Reimbursement"><LeaveApplicationsPage /></Page>} />
        <Route path="/reimbursements" element={<Page name="Reimbursements"><ReimbursementsPage /></Page>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
